import { useEffect, useState } from "react";
import type { FriendAvatar as FriendAvatarData } from "@boo/shared";
import { fetchFriendAvatarUrl } from "@/services/friendApi";

// Blob URLs by friend + version, so a list of rows fetches each photo once.
const photoUrls = new Map<string, Promise<string>>();

function loadPhoto(friendId: string, version: number): Promise<string> {
  const key = `${friendId}:${version}`;
  let url = photoUrls.get(key);
  if (!url) {
    url = fetchFriendAvatarUrl(friendId, version);
    // A failed fetch must not be cached, or the avatar never recovers.
    url.catch(() => photoUrls.delete(key));
    photoUrls.set(key, url);
  }
  return url;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = [...words[0]][0];
  const second = words.length > 1 ? [...words[words.length - 1]][0] : "";
  return (first + second).toUpperCase();
}

/** Stable hue per name, so the same person always gets the same colour. */
function hue(name: string): number {
  let hash = 0;
  for (const ch of name.trim().toLowerCase()) {
    hash = (hash * 31 + ch.codePointAt(0)!) % 360;
  }
  return hash;
}

interface FriendAvatarProps {
  name: string;
  avatar: FriendAvatarData | null;
  /** Needed to fetch a photo; omit with `photoSrc` on public pages. */
  friendId?: string;
  /** Direct image URL (public pages, where the auth header isn't available). */
  photoSrc?: string;
  /** Diameter in px. */
  size?: number;
  className?: string;
}

export default function FriendAvatar({
  name,
  avatar,
  friendId,
  photoSrc,
  size = 32,
  className = "",
}: FriendAvatarProps) {
  const [photo, setPhoto] = useState<{ key: string; url: string } | null>(null);
  const isPhoto = avatar?.kind === "image";
  const version = avatar?.kind === "image" ? avatar.version : 0;
  const key = friendId ? `${friendId}:${version}` : null;

  useEffect(() => {
    if (!isPhoto || photoSrc || !friendId || !key) return;
    let cancelled = false;
    async function load() {
      try {
        const url = await loadPhoto(friendId!, version);
        if (!cancelled) setPhoto({ key: key!, url });
      } catch {
        // Falls back to initials; nothing useful to tell the user about a thumbnail.
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [isPhoto, photoSrc, friendId, version, key]);

  const base = `inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full select-none ${className}`;
  const box = { width: size, height: size };

  if (avatar?.kind === "emoji") {
    return (
      <span
        className={`${base} bg-muted`}
        style={{ ...box, fontSize: size * 0.58, lineHeight: 1 }}
        aria-hidden="true"
      >
        {avatar.emoji}
      </span>
    );
  }

  const src = photoSrc ?? (photo && photo.key === key ? photo.url : null);
  if (isPhoto && src) {
    return (
      <img
        src={src}
        alt=""
        className={`${base} object-cover`}
        style={box}
        draggable={false}
      />
    );
  }

  return (
    <span
      className={`${base} font-semibold text-white`}
      style={{
        ...box,
        fontSize: size * 0.4,
        backgroundColor: `hsl(${hue(name)} 45% 45%)`,
      }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}
